import { FunctionComponent, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardWrapper, Card, Text, Button } from 'src/shared';
import StatusBadge from 'src/shared/statusBadge';
import styled, { keyframes } from 'styled-components';
import { Colors } from 'src/utils/color';
import { Size } from 'src/shared/text';
import { usePolling } from 'src/hooks/usePolling';
import { getJobSummary, getAnalysisJobs } from 'src/services/api';
import type { JobSummary, AnalysisJob } from 'src/types/job';
import { toDisplayStatus } from 'src/types/job';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

const MetricsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 14px;
`;

const MetricCard = styled(Card)`
    padding: 20px;
    border: 2px solid transparent;
`;

const MetricValue = styled.div<{ $color?: string }>`
    font-size: 32px;
    font-weight: 700;
    color: ${p => p.$color || Colors.extra.black};
    margin-bottom: 4px;
`;

const MetricLabel = styled.div`
    font-size: 13px;
    color: ${Colors.extra.dark_gray};
`;

const MetricSub = styled.div`
    font-size: 11px;
    color: ${Colors.extra.dark_gray};
    margin-top: 2px;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const ActionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ActionRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border: 1px solid ${Colors.extra.light_gray};
    border-radius: 8px;
    background: ${Colors.extra.white};
    transition: background 0.1s ease;
    &:hover { background: ${Colors.extra.light_gray}55; }
`;

const ActionInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const ActionTitle = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${Colors.extra.black};
`;

const ActionSub = styled.span`
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
`;

const SmallLink = styled.button`
    background: none;
    border: none;
    color: ${Colors.theme.primary};
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    &:hover { text-decoration: underline; }
`;

const QuickActionsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
`;

const QuickActionCard = styled(Card)`
    padding: 20px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
    transition: all 0.15s ease;
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    }
`;

const ActionIcon = styled.div<{ $bg: string }>`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${p => p.$bg};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
`;

const EmptyDash = styled.div`
    padding: 40px;
    text-align: center;
    border: 1px solid ${Colors.extra.light_gray};
    border-radius: 10px;
`;

const spin = keyframes`from{transform:rotate(0deg);}to{transform:rotate(360deg);}`;
const Spinner = styled.span`
    display: inline-block;
    width: 14px; height: 14px;
    border: 2px solid ${Colors.theme.primary}33;
    border-top-color: ${Colors.theme.primary};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
    margin-right: 6px;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const RetinaTrackPage: FunctionComponent = () => {
    const navigate = useNavigate();
    const [summary, setSummary] = useState<JobSummary | null>(null);
    const [failedJobs, setFailedJobs] = useState<AnalysisJob[]>([]);
    const [needsReview, setNeedsReview] = useState<AnalysisJob[]>([]);
    const [loading, setLoading] = useState(true);

    const [completedToday, setCompletedToday] = useState<AnalysisJob[]>([]);

    const loadDashboard = useCallback(async () => {
        try {
            const [summaryRes, failedRes, reviewRes, completedRes] = await Promise.all([
                getJobSummary(),
                getAnalysisJobs({ page: 1, page_size: 5, status: 'FAILED' }),
                getAnalysisJobs({ page: 1, page_size: 5, status: 'SUCCEEDED', reviewed: 'false' }),
                getAnalysisJobs({ page: 1, page_size: 5, status: 'SUCCEEDED' }),
            ]);
            setSummary(summaryRes);
            setFailedJobs(failedRes?.results ?? failedRes?.data ?? []);
            setNeedsReview(reviewRes?.results ?? reviewRes?.data ?? []);
            setCompletedToday(completedRes?.results ?? completedRes?.data ?? []);
        } catch {
            setSummary({ queued: 0, processing: 0, failed: 0, completed_24h: 0, needs_review: 0, stuck: 0 });
        } finally {
            setLoading(false);
        }
    }, []);

    usePolling(loadDashboard, 12_000);

    const s = summary ?? { completed_24h: 0, processing: 0, queued: 0, failed: 0, needs_review: 0, stuck: 0 };
    const hasActionItems = s.failed > 0 || s.stuck > 0 || s.needs_review > 0;

    return (
        <DashboardWrapper>
            <Container>
                <Header>
                    <div>
                        <Text fontSize={Size.Large} fontWeight="600" color={Colors.extra.black}>
                            Clinical Pipeline Dashboard
                        </Text>
                        <Text fontSize={Size.Small} color={Colors.extra.dark_gray} marginTop={8}>
                            System health and analysis throughput
                        </Text>
                    </div>
                    <Button onClick={() => navigate('/retina/upload')}>Upload Scan</Button>
                </Header>

                {/* Operational metrics */}
                <MetricsGrid>
                    <MetricCard>
                        <MetricValue $color="#059669">{loading ? '—' : s.completed_24h}</MetricValue>
                        <MetricLabel>Completed</MetricLabel>
                        <MetricSub>Last 24 hours</MetricSub>
                    </MetricCard>
                    <MetricCard>
                        <MetricValue $color="#2563EB">{loading ? '—' : s.processing + s.queued}</MetricValue>
                        <MetricLabel>In Progress</MetricLabel>
                        <MetricSub>{s.queued} queued &middot; {s.processing} processing</MetricSub>
                    </MetricCard>
                    <MetricCard>
                        <MetricValue $color="#DC2626">{loading ? '—' : s.failed}</MetricValue>
                        <MetricLabel>Failed</MetricLabel>
                        <MetricSub>Needs attention</MetricSub>
                    </MetricCard>
                    <MetricCard>
                        <MetricValue $color="#D97706">{loading ? '—' : s.needs_review}</MetricValue>
                        <MetricLabel>Needs Review</MetricLabel>
                        <MetricSub>Completed, unreviewed</MetricSub>
                    </MetricCard>
                </MetricsGrid>

                {/* Action Required — ordered by urgency: Failed > Stuck > Needs Review */}
                {!loading && hasActionItems && (
                    <Section>
                        <SectionHeader>
                            <Text fontSize={Size.Medium} fontWeight="600">Action Required</Text>
                        </SectionHeader>

                        <ActionList>
                            {/* Failed */}
                            {failedJobs.map(job => (
                                <ActionRow key={job.id}>
                                    <ActionInfo>
                                        <ActionTitle>
                                            {job.input_data_uri.split('/').pop() ?? 'Unknown scan'}
                                        </ActionTitle>
                                        <ActionSub>
                                            Failed {job.completed_at ? new Date(job.completed_at).toLocaleString() : ''} &middot; {job.error_message?.slice(0, 80) || 'No error details'}
                                        </ActionSub>
                                    </ActionInfo>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <StatusBadge status="Failed" />
                                        <SmallLink onClick={() => navigate('/scans')}>View</SmallLink>
                                    </div>
                                </ActionRow>
                            ))}

                            {/* Stuck (summary count only — we don't have stuck-specific list endpoint yet) */}
                            {s.stuck > 0 && (
                                <ActionRow>
                                    <ActionInfo>
                                        <ActionTitle>{s.stuck} stuck {s.stuck > 1 ? 'analyses' : 'analysis'}</ActionTitle>
                                        <ActionSub>Processing but no heartbeat — may need investigation</ActionSub>
                                    </ActionInfo>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <StatusBadge status="Stuck" />
                                        <SmallLink onClick={() => navigate('/scans')}>View</SmallLink>
                                    </div>
                                </ActionRow>
                            )}

                            {/* Needs Review */}
                            {needsReview.map(job => (
                                <ActionRow key={job.id}>
                                    <ActionInfo>
                                        <ActionTitle>
                                            {job.input_data_uri.split('/').pop() ?? 'Unknown scan'}
                                        </ActionTitle>
                                        <ActionSub>
                                            Completed {job.completed_at ? new Date(job.completed_at).toLocaleString() : ''} — awaiting review
                                        </ActionSub>
                                    </ActionInfo>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <StatusBadge status="Needs Review" />
                                        <SmallLink onClick={() => navigate(`/scans/${job.id}/result`)}>Review</SmallLink>
                                    </div>
                                </ActionRow>
                            ))}
                        </ActionList>

                        {(s.failed > 5 || s.needs_review > 5) && (
                            <SmallLink onClick={() => navigate('/scans')}>View all scans &rarr;</SmallLink>
                        )}
                    </Section>
                )}

                {!loading && !hasActionItems && (
                    <EmptyDash>
                        <Text fontSize={Size.Medium} fontWeight="500" color={Colors.extra.dark_gray}>
                            All clear — no items need attention right now.
                        </Text>
                    </EmptyDash>
                )}

                {/* Recently Completed */}
                {!loading && completedToday.length > 0 && (
                    <Section>
                        <SectionHeader>
                            <Text fontSize={Size.Medium} fontWeight="600">Recently Completed</Text>
                            <SmallLink onClick={() => navigate('/scans')}>View all</SmallLink>
                        </SectionHeader>
                        <ActionList>
                            {completedToday.slice(0, 5).map(job => {
                                const display = toDisplayStatus(job);
                                return (
                                    <ActionRow key={job.id}>
                                        <ActionInfo>
                                            <ActionTitle>
                                                {job.patient_name ? `${job.patient_name} — ` : ''}
                                                {job.input_data_uri.split('/').pop() ?? 'Unknown'}
                                            </ActionTitle>
                                            <ActionSub>
                                                {job.completed_at ? new Date(job.completed_at).toLocaleString() : ''}
                                                {job.reviewed ? ` · Reviewed by ${job.reviewed_by_username || 'clinician'}` : ''}
                                            </ActionSub>
                                        </ActionInfo>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                            <StatusBadge status={display} />
                                            <SmallLink onClick={() => navigate(`/scans/${job.id}/result`)}>View</SmallLink>
                                        </div>
                                    </ActionRow>
                                );
                            })}
                        </ActionList>
                    </Section>
                )}

                {/* Quick Actions */}
                <Section>
                    <Text fontSize={Size.Medium} fontWeight="600">Quick Actions</Text>
                    <QuickActionsGrid>
                        <QuickActionCard onClick={() => navigate('/retina/upload')}>
                            <ActionIcon $bg="#DBEAFE">&#8593;</ActionIcon>
                            <Text fontWeight="500">Upload Scan</Text>
                            <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray}>Single HDF5, max 4 GB</Text>
                        </QuickActionCard>
                        <QuickActionCard onClick={() => navigate('/scans')}>
                            <ActionIcon $bg="#E0E7FF">&#9881;</ActionIcon>
                            <Text fontWeight="500">View Processing Analyses</Text>
                            <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray}>{s.processing + s.queued} active</Text>
                        </QuickActionCard>
                        <QuickActionCard onClick={() => navigate('/scans')}>
                            <ActionIcon $bg="#D1FAE5">&#10003;</ActionIcon>
                            <Text fontWeight="500">View Completed</Text>
                            <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray}>{s.completed_24h} in last 24h</Text>
                        </QuickActionCard>
                    </QuickActionsGrid>
                </Section>
            </Container>
        </DashboardWrapper>
    );
};

export default RetinaTrackPage;
